import { useEffect, useMemo, useState } from "react";
import { neon } from "@/integrations/neon";

interface HealthPayload {
  status: "pending" | "ok" | "error";
  timestamp: string;
  environment: string;
  databaseDriver: string;
  hasSession: boolean;
  message?: string;
}

const createPayload = (overrides: Partial<HealthPayload>): HealthPayload => ({
  status: "pending",
  timestamp: new Date().toISOString(),
  environment: import.meta.env.MODE ?? "unknown",
  databaseDriver: "Neon Serverless Postgres",
  hasSession: false,
  ...overrides,
});

const Healthz = () => {
  const [payload, setPayload] = useState<HealthPayload>(() => createPayload({}));

  useEffect(() => {
    let isMounted = true;

    const run = async () => {
      try {
        const { data, error } = await neon.auth.getSession();

        if (!isMounted) {
          return;
        }

        if (error) {
          setPayload(createPayload({
            status: "error",
            message: error.message,
          }));
          return;
        }

        setPayload(createPayload({
          status: "ok",
          hasSession: Boolean(data.session),
        }));
      } catch (caught) {
        if (!isMounted) {
          return;
        }

        setPayload(createPayload({
          status: "error",
          message: caught instanceof Error ? caught.message : "Unknown health check error",
        }));
      }
    };

    void run();

    return () => {
      isMounted = false;
    };
  }, []);

  const jsonPayload = useMemo(
    () => JSON.stringify(payload, null, 2),
    [payload],
  );

  return (
    <div className="mx-auto max-w-2xl p-6">
      <script
        id="healthz-payload"
        type="application/json"
        // Intentionally exposing structured payload for diagnostics tooling
        dangerouslySetInnerHTML={{ __html: JSON.stringify(payload) }}
      />
      <pre
        aria-label="Application health payload"
        data-status={payload.status}
        className="whitespace-pre-wrap break-all text-sm text-foreground"
      >
        {jsonPayload}
      </pre>
    </div>
  );
};

export default Healthz;
